import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Json } from '@/integrations/supabase/types';

export interface SSOConfig {
  provider: string;
  entityId: string;
  ssoUrl: string;
  certificate: string;
}

interface TenantSettings {
  smartInboxEnabled: boolean;
  smartInboxAggregateOnly: boolean;
  smartInboxNoHrUsage: boolean;
  riskSignalsEvidenceBased: boolean;
  riskSignalsNoPeopleAnalytics: boolean;
  aiHumanApprovalRequired: boolean;
  pilotModeEnabled: boolean;
  mfaRequired: boolean;
  cloudVoiceEnabled: boolean;
  ssoConfig: SSOConfig;
  ipAllowlist: string[];
}

const defaultSettings: TenantSettings = {
  smartInboxEnabled: true,
  smartInboxAggregateOnly: true,
  smartInboxNoHrUsage: true,
  riskSignalsEvidenceBased: true,
  riskSignalsNoPeopleAnalytics: true,
  aiHumanApprovalRequired: true,
  pilotModeEnabled: false,
  mfaRequired: false,
  cloudVoiceEnabled: false,
  ssoConfig: { provider: '', entityId: '', ssoUrl: '', certificate: '' },
  ipAllowlist: [],
};

export function useTenantSettings() {
  const { tenantId } = useAuth();
  const [settings, setSettings] = useState<TenantSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      let query = supabase.from('tenant_settings').select('setting_key, setting_value');
      if (tenantId) {
        query = query.eq('tenant_id', tenantId);
      }
      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        const get = (key: string) =>
          data.find((s) => s.setting_key === key)?.setting_value ?? null;

        const getBool = (key: string, def: boolean): boolean => {
          const val = get(key) as { enabled?: boolean } | null;
          return val?.enabled ?? def;
        };

        const ssoRaw = get('sso_config') as Record<string, string> | null;
        const ipRaw = get('ip_allowlist') as { cidrs?: string[] } | null;

        setSettings({
          smartInboxEnabled: getBool('smart_inbox_enabled', true),
          smartInboxAggregateOnly: getBool('smart_inbox_aggregate_only', true),
          smartInboxNoHrUsage: getBool('smart_inbox_no_hr_usage', true),
          riskSignalsEvidenceBased: getBool('risk_signals_evidence_based', true),
          riskSignalsNoPeopleAnalytics: getBool('risk_signals_no_people_analytics', true),
          aiHumanApprovalRequired: getBool('ai_human_approval_required', true),
          pilotModeEnabled: getBool('pilot_mode_enabled', false),
          mfaRequired: getBool('mfa_required', false),
          cloudVoiceEnabled: getBool('cloud_voice_enabled', false),
          ssoConfig: {
            provider: ssoRaw?.provider ?? '',
            entityId: ssoRaw?.entity_id ?? '',
            ssoUrl: ssoRaw?.sso_url ?? '',
            certificate: ssoRaw?.certificate ?? '',
          },
          ipAllowlist: ipRaw?.cidrs ?? [],
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error fetching tenant settings:', msg);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = async (key: string, enabled: boolean, opts?: { createIfMissing?: boolean }): Promise<boolean> => {
    return updateJsonSetting(key, { enabled }, opts);
  };

  const updateJsonSetting = async (
    key: string,
    value: Record<string, unknown>,
    opts?: { createIfMissing?: boolean },
  ): Promise<boolean> => {
    if (!tenantId) {
      setError('No tenant context');
      return false;
    }
    try {
      const now = new Date().toISOString();
      const { error } = opts?.createIfMissing
        ? await supabase
          .from('tenant_settings')
          .upsert(
            { tenant_id: tenantId, setting_key: key, setting_value: value as Json, category: 'ai', updated_at: now },
            { onConflict: 'tenant_id,setting_key' },
          )
        : await supabase
          .from('tenant_settings')
          .update({ setting_value: value as Json, updated_at: now })
          .eq('setting_key', key)
          .eq('tenant_id', tenantId);
      if (error) throw error;

      await fetchSettings();
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error updating tenant setting:', msg);
      setError(msg);
      return false;
    }
  };

  return {
    settings,
    isLoading,
    error,
    updateSetting,
    updateJsonSetting,
    refetch: fetchSettings,
  };
}
